/**
 * Transitory placeholder slice — Multi-Add Phase 2 (spec §2, §2.1, §17.1).
 *
 * A *transitory placeholder* is the frontend record for a camera the user
 * added in bulk-add but has not authenticated yet. It is NOT a real device:
 * nothing is committed to `room.devices` or `control.device` (agent
 * authority — an injected flag is wiped on the next state-sync tick, spec
 * §1.2) until authentication succeeds. The placeholder lets the tile + "Authenticate"
 * chip render immediately so bulk-add is non-blocking (spec §0 Goal),
 * and is removed once auth lands (spec §5).
 *
 * Per `verified-anchors-6370b897b` (ai.knowledge/platform) + spec §17.1:
 * this slice lives in the FRONTEND package (`device/transitory.ts`), NOT
 * `@studio/models`, because the record carries the frontend `Discovery`
 * type and produces a frontend `Device` (`toSyntheticDevice`). The lower
 * `@studio/models` layer cannot import those (dependency direction).
 *
 * Per spec §2.1 + `verified-anchors-6370b897b`: we mirror the
 * `pendingDevices` timestamp pattern (`@studio/models/device/utils.ts:49`)
 * but WITHOUT its 10s TTL (`isPendingDevice`, expires at 10_000ms). A
 * placeholder must persist until auth-success or an explicit remove — a
 * dedicated no-TTL slice is the whole reason we do not reuse
 * `pendingDevices`. `createdAt` is recorded for diagnostics/ordering and
 * for a later liveness unit (spec §7); this slice applies NO expiry to it.
 *
 * Per `think-in-systems` (ai.knowledge/global): this module is the single
 * source of truth for placeholders. Consumers (W3 canvas merge, W4 auth
 * dispatcher) consume the public API below — `getTransitoryDevice`,
 * `toSyntheticDevice`, `useTransitoryDeviceIds` — rather than reinventing
 * read/synthesis logic.
 *
 * Iris placeholders are also persisted on a bridge as pending StudioDevice
 * records (`transitory-record.ts`); for those this slice is the render cache,
 * rebuilt from state-sync, so they survive a reload or an app restart and show
 * in every Studio client of the room.
 */
// zustand v5 dropped the (selector, equalityFn) overload from plain `create`;
// useTransitoryDeviceIds passes `shallow`, so use the traditional factory that
// keeps that overload (same pattern as the other stores here).
import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
import { Protocol } from '@studio/models/video';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Agent } from '@studio/models/agent';
import type { Model } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { Device, Device$Video } from '#~/device/index.js';
import type { Discovery } from '#~/device/discovery/index.js';

/**
 * Discriminates the auth submit path (spec §4.2 / §5): `onvif` replays
 * `probeOnvifDevice`, `iris` replays `linkIrisDevice`. Kept as a union so
 * future source kinds (spec §7 "generic discovery") can extend it.
 */
export type TransitoryKind = 'onvif' | 'iris';

/**
 * A frontend-only placeholder for an unauthenticated bulk-add camera
 * (spec §2). The `video` field carries a default RTSP state so the tile
 * renders and Advanced Settings opens (`isVideoDevice`, §4) — it is a
 * render-only default and is NEVER committed (spec §2 "NUNCA se commitea").
 */
export interface TransitoryPlaceholder {
  /** Synthetic id (derive via `toTransitoryDeviceId`) — placeholder key + render key. */
  readonly id: Device['id'];

  /** Tile label. */
  readonly name: string;

  /** Auth dispatch discriminator (spec §4.2). */
  readonly transitoryKind: TransitoryKind;

  /** Binding to the discovery for liveness (spec §7). */
  readonly discoveryId: Discovery['id'];

  /** Full discovery, replayed by the auth submit (spec §5) — fits because this is a frontend record. */
  readonly originalDiscovery: Discovery;

  /** Camera host — ONVIF probe / Iris link target (spec §2 table). */
  readonly address: string;

  /** ONVIF service port or Iris link port (~9321). Optional; the RTSP stream port is fixed at 554 in `video`. */
  readonly port?: number;

  /**
   * Default video aspect so `isVideoDevice(toSyntheticDevice(...))` is
   * true (spec §2 / §4; `device/utils.ts:48` checks `device.video != null`).
   * A `rtsp://<address>:554` placeholder — never committed.
   */
  readonly video: NonNullable<Device$Video['video']>;

  /** Creation timestamp (diagnostics / ordering). NO TTL is applied to it (spec §2.1). */
  readonly createdAt: number;

  /**
   * Bridge whose state-sync tree persists this placeholder as a pending
   * StudioDevice record. Absent: kept in memory only, lost on restart.
   */
  readonly hostAgentId?: Agent['id'];

  /** The Studio (room) the camera was added to; its tile shows only there. */
  readonly roomId?: Room['id'];
}

/**
 * A synthetic frontend device produced from a placeholder. It passes
 * `isVideoDevice` and additionally carries `transitoryKind` so consumers
 * (W3 `deriveChipState`, W4 auth dispatcher) can detect "this device is a
 * placeholder" without a `room.devices` lookup (preflight design note #4).
 * The frontend `Device` type itself stays unpolluted — the marker lives
 * here, on the synthetic surface.
 */
export type SyntheticTransitoryDevice = Device$Video & {
  readonly transitoryKind: TransitoryKind;
};

/** Internal store shape — the placeholder map plus its two mutations. */
interface TransitoryStore {
  /** Placeholders keyed by synthetic device id. */
  readonly placeholders: Record<Device['id'], TransitoryPlaceholder>;

  /** Insert or replace a placeholder (set/upsert). */
  readonly set: (placeholder: TransitoryPlaceholder) => void;

  /** Remove a placeholder by id (no-op when absent). */
  readonly unset: (id: Device['id']) => void;
}

/**
 * The reactive zustand store. zustand (not the plain module-level `Map`
 * that backs `pendingDevices`) so the canvas re-renders when a placeholder
 * is added/removed — the bulk-add "tile appears immediately" requirement
 * (spec §0) depends on that reactivity, which a non-subscribed `Map` read
 * does not provide.
 */
export const useTransitoryStore = create<TransitoryStore>()((set) => ({
  placeholders: {},

  set: (placeholder) =>
    // Upsert: spreading replaces an existing entry of the same id, so a
    // re-add (e.g. the discovery re-announced) updates in place rather
    // than duplicating — idempotent on `id` (spec §11 "set/unset idempotent").
    set((state) => ({
      placeholders: { ...state.placeholders, [placeholder.id]: placeholder }
    })),

  unset: (id) =>
    set((state) => {
      // No-op when absent — return the SAME reference so subscribers do
      // not re-render on a remove that changed nothing.
      if (!(id in state.placeholders)) {
        return state;
      }

      const next = { ...state.placeholders };
      delete next[id];

      return { placeholders: next };
    })
}));

// ---------- Public API: imperative accessors (non-React callers) ----------
// `runBulkCommit` (W3 wiring) and the auth dispatcher (W4) run outside the
// React render cycle, so they read/write via `getState()`.

/** Insert or replace a placeholder (set/upsert). */
export const setTransitoryDevice = (placeholder: TransitoryPlaceholder): void =>
  useTransitoryStore.getState().set(placeholder);

/** Remove a placeholder by id (no-op when absent). */
export const unsetTransitoryDevice = (id: Device['id']): void =>
  useTransitoryStore.getState().unset(id);

/** Read a single placeholder by id, or `undefined`. */
export const getTransitoryDevice = (id: T_Maybe<Device['id']>): TransitoryPlaceholder | undefined =>
  id ? useTransitoryStore.getState().placeholders[id] : undefined;

/** Snapshot of all placeholder ids (e.g. the canvas merge in `useDeviceIds`). */
export const getTransitoryDeviceIds = (): Device['id'][] =>
  Object.keys(useTransitoryStore.getState().placeholders) as Device['id'][];

// ---------- Public API: React hooks ----------

/** The reactive placeholder map. */
export const useTransitoryDevices = (): Record<Device['id'], TransitoryPlaceholder> =>
  useTransitoryStore((state) => state.placeholders);

/** A single placeholder by id, reactively. */
export const useTransitoryDevice = (id: T_Maybe<Device['id']>): TransitoryPlaceholder | undefined =>
  useTransitoryStore((state) => (id ? state.placeholders[id] : undefined));

/**
 * The list of placeholder ids, for the canvas merge seam W3 hooks into
 * (`room/hooks.ts::useDeviceIds`'s `appendAndDedup`). `shallow` so a
 * re-render only fires when the id SET changes, not on every store write.
 *
 * @param roomId - When given, keep only that Studio's placeholders (and
 *   unscoped ones), so a pending camera never shows — or links — elsewhere.
 */
export const useTransitoryDeviceIds = (roomId?: T_Maybe<Room['id']>): Device['id'][] =>
  useTransitoryStore(
    (state) =>
      Object.values(state.placeholders)
        .filter((placeholder) => !roomId || !placeholder.roomId || placeholder.roomId === roomId)
        .map((placeholder) => placeholder.id),
    shallow
  );

// ---------- Persisted placeholders (pending records on a bridge) ----------

/**
 * Grace before a persisted placeholder whose record is missing from its
 * bridge's state is dropped. A restarted bridge comes back empty until Studio
 * replays the saved snapshot, so a short absence must not remove the tile.
 */
export const PERSISTED_TRANSITORY_ABSENCE_GRACE_MS = 15_000;

/** When each persisted placeholder's record was first seen missing from its bridge. */
const persistedAbsentSince = new Map<Device['id'], number>();

/**
 * Make the placeholders hosted on `hostAgentId` match the pending records in
 * that bridge's current state (`present`). New or renamed records are upserted
 * (keeping the richer discovery an in-session add already recorded); a record
 * missing for the whole grace window is dropped. Unchanged entries keep their
 * identity, so routine bridge ticks do not re-render the canvas.
 *
 * @param hostAgentId - The bridge whose state was observed.
 * @param present - Placeholders for the pending records in that state.
 * @param now - Injectable clock for deterministic tests.
 * @param deleted - Ids of records this update explicitly deleted. They leave at
 *   once, so a removal or a link on another client converges without the grace.
 */
export const syncPersistedTransitoryDevices = (
  hostAgentId: Agent['id'],
  present: Readonly<Record<Device['id'], TransitoryPlaceholder>>,
  now: number = Date.now(),
  deleted: ReadonlySet<string> = new Set()
): void => {
  useTransitoryStore.setState((state) => {
    let next: Record<Device['id'], TransitoryPlaceholder> | undefined;
    const writable = (): Record<Device['id'], TransitoryPlaceholder> =>
      (next ??= { ...state.placeholders });

    for (const [id, placeholder] of Object.entries(present) as [
      Device['id'],
      TransitoryPlaceholder
    ][]) {
      persistedAbsentSince.delete(id);
      const current = state.placeholders[id];

      if (
        current?.hostAgentId === hostAgentId &&
        current.name === placeholder.name &&
        current.roomId === placeholder.roomId
      ) {
        continue;
      }

      writable()[id] = current
        ? {
            ...current,
            name: placeholder.name,
            hostAgentId,
            ...(placeholder.roomId ? { roomId: placeholder.roomId } : {})
          }
        : placeholder;
    }

    for (const [id, placeholder] of Object.entries(state.placeholders) as [
      Device['id'],
      TransitoryPlaceholder
    ][]) {
      if (placeholder.hostAgentId !== hostAgentId || id in present) {
        continue;
      }

      const since = persistedAbsentSince.get(id) ?? now;
      persistedAbsentSince.set(id, since);

      if (deleted.has(id) || now - since >= PERSISTED_TRANSITORY_ABSENCE_GRACE_MS) {
        persistedAbsentSince.delete(id);
        delete writable()[id];
      }
    }

    return next ? { placeholders: next } : state;
  });
};

/** Drop every placeholder hosted on `hostAgentId` (its connection to this Studio ended). */
export const dropPersistedTransitoryDevices = (hostAgentId: Agent['id']): void => {
  useTransitoryStore.setState((state) => {
    const hosted = (Object.keys(state.placeholders) as Device['id'][]).filter(
      (id) => state.placeholders[id]?.hostAgentId === hostAgentId
    );

    if (hosted.length === 0) {
      return state;
    }

    const placeholders = { ...state.placeholders };
    for (const id of hosted) {
      persistedAbsentSince.delete(id);
      delete placeholders[id];
    }

    return { placeholders };
  });
};

// ---------- Public API: synthesis helpers ----------

/**
 * Placeholder model id — synthetic devices are render-only and never
 * committed (spec §2), so a sentinel model id is acceptable when the
 * discovery carries no resolved model (e.g. an Iris discovery).
 */
const UNRESOLVED_MODEL_ID = 'transitory:unresolved-model' as unknown as Model['id'];

/**
 * Lowest possible ordering rank — mirrors `DeviceDefaults.rank`
 * (`@studio/models/device/constants.ts`) without importing the
 * non-executable-under-jest device tree (`esm-jest-mock-patterns`).
 */
const PLACEHOLDER_RANK = Number.MAX_SAFE_INTEGER;

/**
 * Build the default RTSP video state for a placeholder: `rtsp://<host>:554`
 * (spec §2 table). Satisfies `isVideoDevice` (`device.video != null`).
 */
export const buildPlaceholderVideo = (host: string): NonNullable<Device$Video['video']> => ({
  config: {
    host,
    port: 554,
    protocol: Protocol.RTSP
  }
});

/**
 * Derive the synthetic device id for a discovery. Single source of truth
 * for the id derivation (`think-in-systems`) so W3 does not reinvent it.
 * The `transitory:` prefix namespaces placeholder ids away from real
 * device ids and is NOT a `tmp`/`00000000` transient prefix, so
 * `filterTransients` (`@studio/models/device/utils.ts`) leaves it intact —
 * the canvas merge appends placeholder ids AFTER `filterTransients` anyway.
 */
export const toTransitoryDeviceId = (discoveryId: Discovery['id']): Device['id'] =>
  `transitory:${discoveryId}` as unknown as Device['id'];

/**
 * Synthesize a frontend video device from a placeholder (spec §4). The
 * result passes `isVideoDevice` (carries `video`) so the chip click opens
 * Advanced Settings (`useSettings`/`toCandidate` take the device by
 * argument — no `room.devices` lookup, preflight Anchor 4), and carries
 * `transitoryKind` so the auth dispatcher (W4) and `deriveChipState` (W3)
 * recognise it as a placeholder. NEVER committed.
 */
export const toSyntheticDevice = (
  placeholder: TransitoryPlaceholder
): SyntheticTransitoryDevice => ({
  id: placeholder.id,
  agentId: placeholder.originalDiscovery.agentId,
  modelId: UNRESOLVED_MODEL_ID,
  name: placeholder.name,
  rank: PLACEHOLDER_RANK,
  // Pass through the isVideoDevice-relevant aspect verbatim (spec §2/§4).
  video: placeholder.video,
  transitoryKind: placeholder.transitoryKind
});

// ---------- Liveness — placeholder bound to its discovery (spec §7, §17.2#7) ----------
//
// A placeholder is a frontend-only record bound (via `discoveryId`) to the
// discovery the user bulk-added. The discovery can vanish (camera unplugged,
// leaves the network). Liveness keeps the canvas honest:
//
//   - REACTIVE (both kinds, spec §7): if the user authenticates a discovery
//     that already vanished, the ONVIF probe / Iris link fails with a
//     timeout and the dispatcher surfaces an inline error — zero new
//     plumbing here, the failure already occurs (`auth-dispatch.ts`). A
//     failed auth is never read as proof the camera left: it neither removes
//     the placeholder nor posts the "no longer available" toast.
//
//   - PROACTIVE ONVIF (spec §7, §17.2#7): there is NO `mdns:deviceLost`
//     event @6370b897b — discovery eviction is diff/sweep-based
//     (`room/provider/utils.ts:466-560`, discoveries reconstruct each tick).
//     So we mirror the `pendingDevices` *timestamp* pattern
//     (`@studio/models/device/utils.ts:49`): when a placeholder's bound
//     discoveryId is absent from the discovery set for an absence-confirmed
//     ~15s, a SINGLE module-level sweep timer removes the placeholder and
//     toasts. One timer owns every check — NO per-placeholder timer storms.
//
//   - PROACTIVE IRIS is a documented follow-up (spec §7/§13:
//     `mdns:iris:serviceLost`) and is intentionally NOT implemented here.
//
// Per `reference-rules-in-comments` (ai.knowledge/global).

/**
 * Absence-confirmed debounce before a placeholder bound to a vanished
 * discovery is evicted (spec §7 / §17.2#7). ~15s mirrors the grace baked
 * into the `pendingDevices` timestamp pattern — long enough to ride out
 * mDNS/network flapping (a discovery that blinks out for a tick and comes
 * back must NOT cost the user their tile), short enough that a genuinely
 * gone camera does not linger.
 */
export const TRANSITORY_ABSENCE_DEBOUNCE_MS = 15_000;

/**
 * Cadence of the SINGLE sweep timer (spec §7 "single timer owner"). One
 * module-level interval owns all eviction checks, so the effective eviction
 * latency lands in [DEBOUNCE, DEBOUNCE + cadence). 1s keeps that window
 * tight without per-entry timers.
 */
const LIVENESS_SWEEP_INTERVAL_MS = 1_000;

/**
 * First-absence timestamp per placeholder id: when its bound discovery was
 * first observed missing. A present discovery clears the entry (debounce
 * abort); the sweep evicts only entries that stay past the debounce.
 */
const absentSince = new Map<Device['id'], number>();

/**
 * The placeholder whose Authentication tab is currently open. GUARD (spec
 * §7, CRITICAL race): never evict the placeholder the user is actively
 * authenticating — the camera must not vanish under them mid-submit.
 */
let authTabOpenId: Device['id'] | null = null;

/** The SINGLE sweep timer handle (single owner — spec §7). */
let sweepTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Toast emitter seam. The slice stays framework-agnostic
 * (`think-in-systems`, ai.knowledge/global); the React watcher registers a
 * `notifications/utils.js::post`-backed emitter on mount. Unit tests inject
 * a `jest.fn()` to assert the toast without rendering.
 */
let livenessToast: ((placeholder: TransitoryPlaceholder) => void) | null = null;

/**
 * Analytics emitter seam (parallel to `livenessToast`). The React watcher
 * registers an `emitMultiAddAnalytics`-backed callback on mount; the slice
 * stays framework-agnostic and never imports the analytics primitive
 * directly so it remains pure under jest. Fired ONLY on a real eviction (a
 * placeholder removed by the sweep because its discovery vanished) — never
 * for the auth-success / user-remove paths (those drop the placeholder
 * before the sweep sees it).
 */
let livenessAnalytics: ((placeholder: TransitoryPlaceholder) => void) | null = null;

/**
 * Register (or clear, with `undefined`) the liveness toast emitter. Called
 * by the room-level React watcher; the slice never imports the toast
 * primitive directly so it remains pure under jest.
 */
export const registerTransitoryLivenessToast = (
  emit: ((placeholder: TransitoryPlaceholder) => void) | undefined
): void => {
  livenessToast = emit ?? null;
};

/**
 * Register (or clear, with `undefined`) the liveness analytics emitter.
 * Same lifecycle as `registerTransitoryLivenessToast`.
 */
export const registerTransitoryLivenessAnalytics = (
  emit: ((placeholder: TransitoryPlaceholder) => void) | undefined
): void => {
  livenessAnalytics = emit ?? null;
};

/** Start the single sweep timer iff something is being debounced. */
const ensureLivenessSweepTimer = (): void => {
  if (sweepTimer === null && absentSince.size > 0) {
    sweepTimer = setInterval(runTransitoryLivenessSweep, LIVENESS_SWEEP_INTERVAL_MS);
  }
};

/** Stop the single sweep timer (room teardown / nothing left to watch). */
export const stopTransitoryLivenessSweep = (): void => {
  if (sweepTimer !== null) {
    clearInterval(sweepTimer);
    sweepTimer = null;
  }
};

/**
 * GUARD (spec §7, CRITICAL race): mark (`id`) / clear (`null`) the
 * placeholder whose Authentication tab is open. Setting it CANCELS any
 * in-flight debounce for that placeholder — the tab-open transition itself
 * is a hard "do not evict" signal. Clearing it lets the next discovery tick
 * re-arm the debounce if the discovery is still absent.
 */
export const setTransitoryAuthTabOpen = (id: Device['id'] | null): void => {
  authTabOpenId = id;
  if (id !== null) {
    // Cancel the debounce on the tab-open transition (spec §7).
    absentSince.delete(id);
  }
};

/**
 * PROACTIVE ONVIF liveness signal (spec §7, §17.2#7). Reconcile every
 * placeholder against the set of discovery ids currently present in the
 * room. Diff/sweep-based — driven by the discovery tick, NOT a deviceLost
 * event (which does not resolve @6370b897b). This records first-absence
 * timestamps and arms the single sweep timer; the sweep does the eviction.
 *
 * RACE-ABORT (spec §7): a placeholder removed from the slice mid-debounce —
 * auth success (`auth-dispatch.ts::removePlaceholder`) populates the real
 * device and drops the placeholder, or the user removes it — must also drop
 * its absence entry so the sweep can never evict a just-authenticated
 * camera. The auth-success path and this eviction path race on the same id.
 *
 * HOST-KEYED SURVIVAL: a placeholder also counts as PRESENT while ANY
 * discovery at its `address` is present — not just its exact bound
 * `discoveryId`. A dual-transport camera (e.g. NDI + ONVIF twins on one IP)
 * announces per transport, and the announcements flap INDEPENDENTLY; a
 * re-announcement can also mint a NEW discovery id, so the bound id may
 * never return even though the camera is still on the network (its twin —
 * or its own new id — is right there in the set). Keying survival on the
 * exact id evicted such placeholders while the camera sat healthy on the
 * LAN. Host-keying is strictly weaker: every eviction it allows (camera
 * absent on ALL transports past the debounce) was also an eviction before,
 * so it removes false evictions without adding any. Known accepted trade:
 * if DHCP hands the address to a different device mid-debounce, the
 * placeholder survives a little longer — benign within a placeholder's
 * lifetime. Per `reference-rules-in-comments` (ai.knowledge/global).
 *
 * @param presentDiscoveryIds - ids of discoveries currently in the room.
 * @param presentHosts - addresses of discoveries currently in the room.
 * @param now - injectable clock for deterministic tests (defaults to wall).
 */
export const observeTransitoryLiveness = (
  presentDiscoveryIds: ReadonlySet<Discovery['id']>,
  presentHosts: ReadonlySet<string> = new Set(),
  now: number = Date.now()
): void => {
  const { placeholders } = useTransitoryStore.getState();

  for (const id of Object.keys(placeholders) as Device['id'][]) {
    const placeholder = placeholders[id]!;

    // A persisted pending camera lives until the user removes or links it.
    if (placeholder.hostAgentId) {
      absentSince.delete(id);
      continue;
    }

    // HOST-KEYED SURVIVAL (see doc above): the exact bound id OR any
    // discovery at the placeholder's address keeps it alive. Empty-address
    // placeholders fall back to id-only keying.
    const hostPresent = placeholder.address !== '' && presentHosts.has(placeholder.address);

    if (presentDiscoveryIds.has(placeholder.discoveryId) || hostPresent) {
      // Discovery present (or back after a blip) → abort any pending debounce.
      absentSince.delete(id);
    } else if (!absentSince.has(id) && id !== authTabOpenId) {
      // Bound discovery missing this tick → arm the debounce. The tab-open
      // placeholder is never armed (GUARD, spec §7).
      absentSince.set(id, now);
    }
  }

  // RACE-ABORT: drop absence entries whose placeholder no longer exists
  // (auth success / user remove). Never evict what is already gone.
  for (const id of [...absentSince.keys()]) {
    if (!(id in placeholders)) {
      absentSince.delete(id);
    }
  }

  ensureLivenessSweepTimer();
};

/**
 * The SINGLE-owner eviction sweep (spec §7). For each placeholder whose
 * bound discovery has stayed absent past the debounce: re-check it STILL
 * exists (race guard) and its auth tab is NOT open (tab-open guard), then
 * evict — remove from the slice and toast "device no longer available".
 *
 * Re-checking existence here is the second half of the race-abort: even if
 * an absence entry survived to fire, auth success having removed the
 * placeholder means there is nothing to evict.
 *
 * @param now - injectable clock for deterministic tests (defaults to wall).
 */
export const runTransitoryLivenessSweep = (now: number = Date.now()): void => {
  const { placeholders } = useTransitoryStore.getState();

  for (const [id, since] of [...absentSince.entries()]) {
    const placeholder = placeholders[id];

    // Race guard: gone from the slice (auth success / user remove) → abort.
    // A placeholder persisted since it was armed is never evicted either.
    if (!placeholder || placeholder.hostAgentId) {
      absentSince.delete(id);
      continue;
    }

    // Tab-open guard: never evict the placeholder under active auth (§7).
    if (id === authTabOpenId) {
      continue;
    }

    // Debounce not yet elapsed → keep waiting (re-checked next sweep).
    if (now - since < TRANSITORY_ABSENCE_DEBOUNCE_MS) {
      continue;
    }

    // Absence confirmed → evict the placeholder and toast (spec §7).
    absentSince.delete(id);
    unsetTransitoryDevice(id);
    livenessToast?.(placeholder);
    // Analytics: the liveness watcher removed a placeholder whose camera
    // vanished. Distinct from auth-success / user-remove (those drop the
    // placeholder before the sweep runs, so they never reach here).
    livenessAnalytics?.(placeholder);
  }

  // Nothing left to watch → release the single timer.
  if (absentSince.size === 0) {
    stopTransitoryLivenessSweep();
  }
};

/**
 * Test-only: reset all module-level liveness state so jest cases do not
 * leak the absence map / tab guard / sweep timer between runs. Exported so
 * the test `beforeEach` can call it; production never needs it (the React
 * watcher's unmount cleanup calls `stopTransitoryLivenessSweep`).
 */
export const __resetTransitoryLivenessForTest = (): void => {
  stopTransitoryLivenessSweep();
  absentSince.clear();
  persistedAbsentSince.clear();
  authTabOpenId = null;
  livenessToast = null;
  livenessAnalytics = null;
};
